import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getReports, ScraperReport, buildEventDashboardLinkState } from '../services/scraperOrchestration.ts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './ScraperReports.css';

type TriggerFilter = 'all' | 'manual' | 'scheduled' | 'manual_local' | 'scheduled_local' | 'locally' | 'unknown';
type TimeRange = 'week' | 'month' | 'year' | 'all';
type ChartBucket = 'day' | 'week' | 'month';

const INITIAL_VISIBLE_ROWS = 25;
const LOAD_MORE_ROWS = 50;

const isSuccessfulReport = (report: ScraperReport) =>
  ['completed', 'success'].includes((report.status || '').toLowerCase());

const normalizeTriggerType = (triggerType?: string): Exclude<TriggerFilter, 'all'> => {
  const normalized = (triggerType || '').toLowerCase().trim();
  if (
    normalized === 'scheduled' ||
    normalized === 'manual' ||
    normalized === 'manual_local' ||
    normalized === 'scheduled_local' ||
    normalized === 'locally'
  ) {
    return normalized;
  }
  return 'unknown';
};

const formatTriggerLabel = (triggerType?: string) => {
  const normalized = normalizeTriggerType(triggerType);
  if (normalized === 'scheduled') return 'Scheduled';
  if (normalized === 'scheduled_local') return 'Scheduled local';
  if (normalized === 'manual_local') return 'Manual local';
  if (normalized === 'locally') return 'Locally';
  if (normalized === 'manual') return 'Manual';
  return 'Unknown';
};

const getReportTimestamp = (report: ScraperReport) => {
  const parsed = new Date(report.start_time).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatDuration = (seconds?: number) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
};

const getStartOfWeek = (date: Date) => {
  const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (weekStart.getDay() + 6) % 7;
  weekStart.setDate(weekStart.getDate() - offset);
  return weekStart;
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date: Date) => {
  const result = startOfDay(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date: Date, months: number) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const alignDateToBucket = (date: Date, bucket: ChartBucket) => {
  if (bucket === 'month') {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
  if (bucket === 'week') {
    return getStartOfWeek(date);
  }
  return startOfDay(date);
};

const getNextBucketDate = (date: Date, bucket: ChartBucket) => {
  if (bucket === 'month') return addMonths(date, 1);
  if (bucket === 'week') return addDays(date, 7);
  return addDays(date, 1);
};

const formatBucketLabel = (date: Date, bucket: ChartBucket) => {
  if (bucket === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  if (bucket === 'week') {
    const end = new Date(date);
    end.setDate(end.getDate() + 6);
    return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatBucketTickLabel = (date: Date, bucket: ChartBucket) => {
  if (bucket === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getSelectedRangeBounds = (
  useDateRange: boolean,
  startDate: string,
  endDate: string,
  range: TimeRange,
  reports: ScraperReport[]
): { start: Date; end: Date } | null => {
  const validReportTimes = reports
    .map((report) => getReportTimestamp(report))
    .filter((timestamp) => timestamp > 0)
    .sort((a, b) => a - b);

  if (useDateRange && startDate && endDate) {
    const start = startOfDay(new Date(startDate));
    const end = endOfDay(new Date(endDate));
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end >= start) {
      return { start, end };
    }
  }

  if (useDateRange) {
    const fallbackStart = startDate ? startOfDay(new Date(startDate)) : null;
    const fallbackEnd = endDate ? endOfDay(new Date(endDate)) : null;
    const firstReport = validReportTimes.length > 0 ? startOfDay(new Date(validReportTimes[0])) : null;
    const lastReport = validReportTimes.length > 0 ? endOfDay(new Date(validReportTimes[validReportTimes.length - 1])) : null;

    const start = fallbackStart ?? firstReport ?? fallbackEnd;
    const end = fallbackEnd ?? lastReport ?? fallbackStart;
    if (start && end && end >= start) {
      return { start, end };
    }
    return null;
  }

  if (range === 'all') {
    if (validReportTimes.length === 0) return null;
    return {
      start: startOfDay(new Date(validReportTimes[0])),
      end: endOfDay(new Date(validReportTimes[validReportTimes.length - 1])),
    };
  }

  const days = range === 'year' ? 365 : range === 'month' ? 30 : 7;
  const end = endOfDay(new Date());
  const start = startOfDay(addDays(end, -(days - 1)));
  return { start, end };
};

const buildChartData = (
  reports: ScraperReport[],
  bucket: ChartBucket,
  rangeBounds: { start: Date; end: Date } | null
) => {
  const points = new Map<string, {
    sortTime: number;
    tickLabel: string;
    tooltipLabel: string;
    events: number;
    duplicates: number;
    runs: number;
    successfulRuns: number;
  }>();

  if (rangeBounds) {
    let cursor = alignDateToBucket(rangeBounds.start, bucket);
    const finalBucket = alignDateToBucket(rangeBounds.end, bucket);

    while (cursor.getTime() <= finalBucket.getTime()) {
      points.set(String(cursor.getTime()), {
        sortTime: cursor.getTime(),
        tickLabel: formatBucketTickLabel(cursor, bucket),
        tooltipLabel: formatBucketLabel(cursor, bucket),
        events: 0,
        duplicates: 0,
        runs: 0,
        successfulRuns: 0,
      });
      cursor = getNextBucketDate(cursor, bucket);
    }
  }

  reports.forEach((report) => {
    const reportDate = new Date(report.start_time);
    if (Number.isNaN(reportDate.getTime())) return;

    const bucketDate = alignDateToBucket(reportDate, bucket);
    const key = String(bucketDate.getTime());

    if (!points.has(key)) {
      points.set(key, {
        sortTime: bucketDate.getTime(),
        tickLabel: formatBucketTickLabel(bucketDate, bucket),
        tooltipLabel: formatBucketLabel(bucketDate, bucket),
        events: 0,
        duplicates: 0,
        runs: 0,
        successfulRuns: 0,
      });
    }

    const current = points.get(key)!;
    current.events += report.events?.successful || 0;
    current.duplicates += report.events?.duplicates || 0;
    current.runs += 1;
    if (isSuccessfulReport(report)) current.successfulRuns += 1;
  });

  return Array.from(points.values())
    .sort((a, b) => a.sortTime - b.sortTime)
    .map((point) => ({
      tickLabel: point.tickLabel,
      tooltipLabel: point.tooltipLabel,
      events: point.events,
      duplicates: point.duplicates,
      runs: point.runs,
      successfulRuns: point.successfulRuns,
      successRate: point.runs > 0 ? Math.round((point.successfulRuns / point.runs) * 100) : 0,
    }));
};

const ErrorDetails: React.FC<{ errors: string[] }> = ({ errors }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (errors.length === 0) {
    return <span className="no-errors">-</span>;
  }

  return (
    <div className="error-details-container">
      <span 
        className="error-count" 
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        {errors.length} error{errors.length > 1 ? 's' : ''}
      </span>
      {isOpen && (
        <div 
          className="error-details-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="error-details-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-details-header">
              <h4>Error Details</h4>
              <button onClick={() => setIsOpen(false)} className="error-details-close">×</button>
            </div>
            <div className="error-details-list">
              {errors.map((error, index) => (
                <div key={index} className="error-detail-item">
                  <pre>{error}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RunNotes: React.FC<{
  messages?: string[];
  skippedEvents?: Array<{ url: string; reason: string }>;
  runId?: string;
}> = ({ messages = [], skippedEvents = [], runId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const noteCount = messages.length + (skippedEvents.length > 0 ? 1 : 0);

  if (noteCount === 0) {
    return <span className="no-errors">-</span>;
  }

  return (
    <div className="run-notes-container">
      <span
        className="run-notes-count"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
        title="Expected filtering — not an error"
      >
        {noteCount} note{noteCount > 1 ? 's' : ''}
      </span>
      {isOpen && (
        <div
          className="error-details-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="error-details-content run-notes-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-details-header">
              <h4>Run Notes</h4>
              <button onClick={() => setIsOpen(false)} className="error-details-close">×</button>
            </div>
            <div className="error-details-list">
              {runId && (
                <div className="run-note-item run-note-summary">
                  Run ID: {runId}
                </div>
              )}
              {messages.map((message, index) => (
                <div key={`msg-${index}`} className="run-note-item">
                  {message}
                </div>
              ))}
              {skippedEvents.length > 0 && (
                <div className="run-note-item">
                  <strong>{skippedEvents.length} skipped event(s)</strong>
                  <div className="skipped-events-list">
                    {skippedEvents.slice(0, 25).map((item, index) => (
                      <div key={`skip-${index}`} className="skipped-event-row">
                        <div className="skipped-event-reason">{item.reason}</div>
                        <a href={item.url} target="_blank" rel="noreferrer" className="skipped-event-url">
                          {item.url}
                        </a>
                      </div>
                    ))}
                    {skippedEvents.length > 25 && (
                      <div className="skipped-event-more">
                        …and {skippedEvents.length - 25} more skipped (see Firestore run doc)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ScraperReports: React.FC = () => {
  const [reports, setReports] = useState<ScraperReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedScraper, setSelectedScraper] = useState<string>('');
  const [selectedTriggerType, setSelectedTriggerType] = useState<TriggerFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [useDateRange, setUseDateRange] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chartTimeRange, setChartTimeRange] = useState<TimeRange>('week');
  const [chartBucket, setChartBucket] = useState<ChartBucket>('day');
  const [visibleRows, setVisibleRows] = useState<number>(INITIAL_VISIBLE_ROWS);
  const [lastLoadedAt, setLastLoadedAt] = useState<string>('');

  const loadReports = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      let days: number | undefined;
      let startDateParam: string | undefined;
      let endDateParam: string | undefined;

      if (useDateRange) {
        if (startDate) {
          startDateParam = new Date(startDate).toISOString();
        }
        if (endDate) {
          const inclusiveEndDate = new Date(endDate);
          inclusiveEndDate.setHours(23, 59, 59, 999);
          endDateParam = inclusiveEndDate.toISOString();
        }
      } else if (chartTimeRange !== 'all') {
        days = chartTimeRange === 'year' ? 365 : chartTimeRange === 'month' ? 30 : 7;
      }

      const data = await getReports(
        undefined,
        undefined,
        days,
        startDateParam,
        endDateParam,
        undefined
      );

      const sortedReports = [...data.reports].sort((a, b) => getReportTimestamp(b) - getReportTimestamp(a));
      setReports(sortedReports);
      setLastLoadedAt(data.timestamp || new Date().toISOString());
    } catch (error: any) {
      console.error('Error loading reports:', error);
      const errorMessage = error?.message || String(error);
      setError(`Failed to load reports: ${errorMessage}. Check browser console for details.`);
    } finally {
      setLoading(false);
    }
  }, [chartTimeRange, endDate, startDate, useDateRange]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadReports();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadReports]);

  const getEventLinkState = (report: ScraperReport) => {
    const startTime = new Date(report.start_time);
    const endTime = report.end_time ? new Date(report.end_time) : new Date(startTime.getTime() + (report.duration_seconds || 0) * 1000);
    return buildEventDashboardLinkState(report.scraper, startTime, endTime);
  };

  const uniqueScrapers = useMemo(
    () => Array.from(new Set(reports.map((report) => report.scraper).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [reports]
  );

  const availableTriggers = useMemo(
    () => Array.from(new Set(reports.map((report) => normalizeTriggerType(report.trigger_type)))),
    [reports]
  );

  useEffect(() => {
    setVisibleRows(INITIAL_VISIBLE_ROWS);
  }, [selectedScraper, selectedTriggerType, chartTimeRange, chartBucket, startDate, endDate, useDateRange]);

  useEffect(() => {
    if (selectedScraper && !uniqueScrapers.includes(selectedScraper)) {
      setSelectedScraper('');
    }
  }, [selectedScraper, uniqueScrapers]);

  const filteredReports = useMemo(() => (
    reports.filter((report) => {
      const matchesScraper = !selectedScraper || report.scraper === selectedScraper;
      const matchesTrigger =
        selectedTriggerType === 'all' || normalizeTriggerType(report.trigger_type) === selectedTriggerType;
      return matchesScraper && matchesTrigger;
    })
  ), [reports, selectedScraper, selectedTriggerType]);

  const chartRangeBounds = useMemo(
    () => getSelectedRangeBounds(useDateRange, startDate, endDate, chartTimeRange, filteredReports),
    [chartTimeRange, endDate, filteredReports, startDate, useDateRange]
  );

  const chartData = useMemo(
    () => buildChartData(filteredReports, chartBucket, chartRangeBounds),
    [chartBucket, chartRangeBounds, filteredReports]
  );

  const overview = useMemo(() => {
    const totals = filteredReports.reduce((acc, report) => {
      acc.runs += 1;
      acc.events += report.events?.successful || 0;
      acc.duplicates += report.events?.duplicates || 0;
      acc.failures += report.events?.failed || 0;
      acc.duration += report.duration_seconds || 0;
      if (isSuccessfulReport(report)) acc.successfulRuns += 1;
      return acc;
    }, {
      runs: 0,
      successfulRuns: 0,
      events: 0,
      duplicates: 0,
      failures: 0,
      duration: 0,
    });

    return {
      ...totals,
      successRate: totals.runs > 0 ? Math.round((totals.successfulRuns / totals.runs) * 100) : 0,
      averageDuration: totals.runs > 0 ? totals.duration / totals.runs : 0,
    };
  }, [filteredReports]);

  const visibleReportsForTable = useMemo(
    () => filteredReports.slice(0, visibleRows),
    [filteredReports, visibleRows]
  );

  const hasMore = visibleRows < filteredReports.length;

  const activeRangeLabel = useMemo(() => {
    if (useDateRange) {
      if (startDate && endDate) return `${startDate} to ${endDate}`;
      if (startDate) return `From ${startDate}`;
      if (endDate) return `Until ${endDate}`;
      return 'Custom range';
    }
    if (chartTimeRange === 'week') return 'Last 7 days';
    if (chartTimeRange === 'month') return 'Last 30 days';
    if (chartTimeRange === 'year') return 'Last 365 days';
    return 'All time';
  }, [chartTimeRange, endDate, startDate, useDateRange]);

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div className="scraper-reports">
      <div className="reports-page-header">
        <div>
          <p className="reports-eyebrow">Reports</p>
          <h2>Scraper performance</h2>
          <p className="reports-subtitle">
            Cleaner trends, working filters, and a recent-runs table that stays separate from the charts.
          </p>
        </div>
        <div className="reports-page-actions">
          <span className="reports-last-loaded">Updated {formatDate(lastLoadedAt)}</span>
          <button onClick={loadReports} className="refresh-button">Refresh</button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Could not load scraper reports.</strong>
          <span>{error}</span>
          <button onClick={loadReports} className="retry-button">Retry</button>
        </div>
      )}

      <div className="reports-controls-card">
        <div className="reports-range-switcher">
          <div className="reports-control-group">
            <span className="section-label">Quick range</span>
            <div className="segmented-control">
              {[
                { value: 'week', label: '1W' },
                { value: 'month', label: '1M' },
                { value: 'year', label: '1Y' },
                { value: 'all', label: 'All' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={chartTimeRange === option.value ? 'active' : ''}
                  onClick={() => {
                    setUseDateRange(false);
                    setChartTimeRange(option.value as TimeRange);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="reports-control-group">
            <span className="section-label">Graph datapoints</span>
            <div className="segmented-control">
              {[
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={chartBucket === option.value ? 'active' : ''}
                  onClick={() => setChartBucket(option.value as ChartBucket)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="reports-filters-grid">
          <label className="filter-field">
            <span>Scraper</span>
            <select
              value={selectedScraper}
              onChange={(e) => setSelectedScraper(e.target.value)}
              className="filter-select"
            >
              <option value="">All scrapers</option>
              {uniqueScrapers.map((scraper) => (
                <option key={scraper} value={scraper}>{scraper}</option>
              ))}
            </select>
          </label>

          <label className="filter-field">
            <span>Trigger type</span>
            <select
              value={selectedTriggerType}
              onChange={(e) => setSelectedTriggerType(e.target.value as TriggerFilter)}
              className="filter-select"
            >
              <option value="all">All triggers</option>
              {availableTriggers.includes('manual') && <option value="manual">Manual</option>}
              {availableTriggers.includes('scheduled') && <option value="scheduled">Scheduled</option>}
              {availableTriggers.includes('scheduled_local') && <option value="scheduled_local">Scheduled local</option>}
              {availableTriggers.includes('manual_local') && <option value="manual_local">Manual local</option>}
              {availableTriggers.includes('locally') && <option value="locally">Locally</option>}
              {availableTriggers.includes('unknown') && <option value="unknown">Unknown</option>}
            </select>
          </label>

          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={useDateRange}
              onChange={(e) => setUseDateRange(e.target.checked)}
            />
            <span>Use custom date range</span>
          </label>
        </div>

        {useDateRange && (
          <div className="reports-custom-range">
            <label className="filter-field">
              <span>Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-date"
              />
            </label>
            <label className="filter-field">
              <span>End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-date"
              />
            </label>
            <button
              type="button"
              className="ghost-button"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setUseDateRange(false);
              }}
            >
              Clear custom range
            </button>
          </div>
        )}

        <div className="reports-active-state">
          <span className="active-pill">{activeRangeLabel}</span>
          <span className="active-pill">By {chartBucket}</span>
          {selectedScraper && <span className="active-pill">{selectedScraper}</span>}
          {selectedTriggerType !== 'all' && <span className="active-pill">{formatTriggerLabel(selectedTriggerType)}</span>}
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <span className="stat-label">Matching runs</span>
          <span className="stat-value-large">{overview.runs}</span>
        </div>
        <div className="summary-card">
          <span className="stat-label">Successful runs</span>
          <span className="stat-value-large">{overview.successfulRuns}</span>
        </div>
        <div className="summary-card">
          <span className="stat-label">Success rate</span>
          <span className={`stat-value-large ${overview.successRate >= 90 ? 'success' : overview.successRate >= 70 ? 'warning' : 'error'}`}>
            {overview.successRate}%
          </span>
        </div>
        <div className="summary-card">
          <span className="stat-label">Events uploaded</span>
          <span className="stat-value-large">{overview.events}</span>
        </div>
        <div className="summary-card">
          <span className="stat-label">Duplicates</span>
          <span className="stat-value-large">{overview.duplicates}</span>
        </div>
        <div className="summary-card">
          <span className="stat-label">Avg. duration</span>
          <span className="stat-value-large">{formatDuration(overview.averageDuration)}</span>
        </div>
      </div>

      {filteredReports.length > 0 ? (
        <div className="reports-chart-grid">
          <div className="chart-panel">
            <div className="chart-panel-header">
              <div>
                <h3>Events over time</h3>
                <p>Uses the full filtered dataset, not just the visible table rows.</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="eventsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="tickLabel"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                  interval="preserveStartEnd"
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.tooltipLabel ?? ''} />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="#2563eb"
                  strokeWidth={3}
                  fill="url(#eventsGradient)"
                  name="Events uploaded"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-panel">
            <div className="chart-panel-header">
              <div>
                <h3>Run quality</h3>
                <p>Successful runs vs total runs for the same selected filters.</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barGap={8}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="tickLabel"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                  interval="preserveStartEnd"
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.tooltipLabel ?? ''} />
                <Bar dataKey="runs" fill="#cbd5e1" radius={[8, 8, 0, 0]} name="All runs" />
                <Bar dataKey="successfulRuns" fill="#10b981" radius={[8, 8, 0, 0]} name="Successful runs" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No reports match these filters</h3>
          <p>Try a broader date range, another trigger type, or clear the scraper filter.</p>
        </div>
      )}

      <div className="reports-table-container">
        <div className="reports-table-toolbar">
          <div>
            <h3>Recent runs</h3>
            <p>{filteredReports.length} matching runs</p>
          </div>
        </div>
        <table className="reports-table">
          <thead>
            <tr>
              <th>Scraper</th>
              <th>Start</th>
              <th>Dur.</th>
              <th>Status</th>
              <th>Trigger</th>
              <th>Events</th>
              <th>Link</th>
              <th>Dups</th>
              <th>Merged</th>
              <th>Failed</th>
              <th>Skipped</th>
              <th>Notes / Errors</th>
            </tr>
          </thead>
          <tbody>
            {visibleReportsForTable.length === 0 ? (
              <tr>
                <td colSpan={12} className="no-reports">
                  No reports found for the selected period.
                </td>
              </tr>
            ) : (
              visibleReportsForTable.map((report, index) => (
                <tr key={report.run_id || `${report.scraper}-${report.start_time}-${index}`} className={isSuccessfulReport(report) ? 'success' : 'error'}>
                  <td className="scraper-name">{report.scraper}</td>
                  <td>{formatDate(report.start_time)}</td>
                  <td>{formatDuration(report.duration_seconds)}</td>
                  <td>
                    <span className={`status-badge ${report.status}`}>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    <span className={`trigger-badge ${normalizeTriggerType(report.trigger_type)}`}>
                      {formatTriggerLabel(report.trigger_type)}
                    </span>
                  </td>
                  <td>
                    <span className="events-count">
                      {report.events.successful} / {report.events.total}
                      {report.total_urls != null && report.total_urls !== report.events.total && (
                        <span className="total-urls-hint" title="URLs attempted in this run">
                          {' '}(of {report.total_urls} URLs)
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    {report.events.successful > 0 ? (
                      <Link 
                        to="/admin"
                        state={getEventLinkState(report)}
                        className="events-link"
                        title={`View ${report.events.successful} events from this scrape`}
                      >
                        View
                      </Link>
                    ) : (
                      <span className="no-events-link">-</span>
                    )}
                  </td>
                  <td>{report.events.duplicates}</td>
                  <td>{report.events.merged || 0}</td>
                  <td>{report.events.failed}</td>
                  <td>{report.events.skipped ?? 0}</td>
                  <td>
                    {report.errors.length > 0 ? (
                      <ErrorDetails errors={report.errors.map((err) =>
                        report.run_id ? `[Run: ${report.run_id}] ${err}` : err
                      )} />
                    ) : (report.messages?.length || report.skipped_events?.length || (report.events.skipped ?? 0) > 0) ? (
                      <RunNotes
                        messages={report.messages}
                        skippedEvents={report.skipped_events}
                        runId={report.run_id}
                      />
                    ) : (
                      <span className="no-errors">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {hasMore && filteredReports.length > 0 && (
          <div className="show-more-container">
            <button
              type="button"
              onClick={() => setVisibleRows((current) => current + LOAD_MORE_ROWS)}
              className="show-more-button"
            >
              Show 50 more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
